<template>
  <div class="pricing-root">
    <div class="pricing-orb pricing-orb-a" aria-hidden="true"></div>
    <div class="pricing-orb pricing-orb-b" aria-hidden="true"></div>

    <header class="pricing-nav">
      <div class="pricing-nav-inner">
        <RouterLink to="/" class="pricing-logo" aria-label="BeyondWebsite.ai home">
          <img :src="brandLogo" alt="BeyondWebsite.ai" />
        </RouterLink>
        <nav class="pricing-nav-center" aria-label="Main navigation">
          <RouterLink to="/" class="pricing-nav-pill">Home</RouterLink>
          <RouterLink to="/pricing" class="pricing-nav-pill">Pricing</RouterLink>
        </nav>
        <div class="pricing-nav-actions">
          <RouterLink to="/login" class="pricing-signin">Sign in</RouterLink>
        </div>
      </div>
    </header>

    <main>
      <section class="pricing-hero">
        <p class="pricing-eyebrow">Pricing</p>
        <h1>
          One platform for your website,
          <span>visibility, and growth.</span>
        </h1>
        <p class="pricing-sub">
          BeyondWebsite.ai combines an AI-maintained website, SEO, AEO, GEO,
          search indexing, blog generation, and social distribution from one source of truth.
        </p>
      </section>

      <section class="pricing-section" aria-labelledby="plans-heading">
        <div class="pricing-section-head">
          <p class="pricing-eyebrow">Plans</p>
          <h2 id="plans-heading">Start with one site. Scale into a growth system.</h2>
        </div>

        <div class="pricing-grid">
          <article
            v-for="plan in plans"
            :key="plan.name"
            class="pricing-card glass"
            :class="{ 'pricing-card-featured': plan.featured }"
          >
            <div v-if="plan.featured" class="pricing-badge">Recommended</div>
            <p class="pricing-plan-label">{{ plan.label }}</p>
            <h3>{{ plan.name }}</h3>
            <div class="pricing-price">
              <span v-if="plan.originalPrice" class="pricing-price-original">{{ plan.originalPrice }}</span>
              <span>{{ plan.price }}</span>
              <small v-if="plan.period">{{ plan.period }}</small>
            </div>
            <p class="pricing-plan-copy">{{ plan.copy }}</p>

            <RouterLink
              v-if="plan.ctaRoute"
              :to="plan.ctaRoute"
              class="pricing-cta"
              :class="{ 'pricing-cta-primary': plan.featured }"
            >
              {{ plan.cta }}
            </RouterLink>
            <a
              v-else
              :href="contactHref"
              class="pricing-cta"
              :class="{ 'pricing-cta-primary': plan.featured }"
            >
              {{ plan.cta }}
            </a>

            <ul class="pricing-features">
              <li v-for="feature in plan.features" :key="feature">
                <span aria-hidden="true">✓</span>
                {{ feature }}
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section class="pricing-section" aria-labelledby="addons-heading">
        <div class="pricing-section-head pricing-section-head-wide">
          <p class="pricing-eyebrow">Add-ons</p>
          <h2 id="addons-heading">Add support where it changes the outcome.</h2>
          <p>
            Keep subscriptions simple, then add launch help or long-term hosting
            when the customer wants us to own more of the operating burden.
          </p>
        </div>

        <div class="pricing-addon-grid">
          <article class="pricing-addon-card glass">
            <div class="pricing-addon-icon">↗</div>
            <div>
              <p class="pricing-plan-label">Launch add-on</p>
              <h3>Onboarding website</h3>
              <p>
                Done-with-you or done-for-you onboarding for teams that want us to
                set up the website, brand details, SEO/AEO/GEO foundation, and publishing flow.
              </p>
              <a :href="contactHref" class="pricing-inline-link">Contact us to onboard →</a>
            </div>
          </article>

          <article class="pricing-addon-card glass">
            <div class="pricing-addon-icon">+</div>
            <div>
              <p class="pricing-plan-label">Usage add-on</p>
              <h3>Extra growth capacity</h3>
              <p>
                Add more AI edits, blog posts, social posts, or visibility workflows
                when your growth cadence needs more than the plan includes.
              </p>
              <a :href="contactHref" class="pricing-inline-link">Contact us for capacity →</a>
            </div>
          </article>
        </div>
      </section>

      <section class="pricing-section pricing-value" aria-labelledby="value-heading">
        <div class="pricing-value-card glass">
          <p class="pricing-eyebrow">Why this is different</p>
          <h2 id="value-heading">You are not buying another website builder.</h2>
          <div class="pricing-value-grid">
            <div>
              <strong>Website</strong>
              <span>Build, edit, publish, version, and maintain one live site.</span>
            </div>
            <div>
              <strong>Visibility</strong>
              <span>SEO, AEO, GEO, technical checks, and search indexing workflows.</span>
            </div>
            <div>
              <strong>Distribution</strong>
              <span>Blog and social content generated from the website as source of truth.</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { RouterLink } from 'vue-router'
import brandLogo from '../assets/bw-logo-wordmark.svg'

const contactHref = 'mailto:sales@beyondwebsite.ai?subject=BeyondWebsite.ai%20Enterprise%20or%20add-on%20enquiry'

const plans = [
  {
    name: 'Starter',
    label: 'For lean teams',
    price: '$99',
    originalPrice: '$198',
    period: '/month',
    copy: 'Launch and maintain one growth-ready website with the essentials included.',
    cta: 'Start building',
    ctaRoute: '/login',
    features: [
      '1 published website',
      'Lifetime free hosting',
      'Custom domain',
      'Unlimited manual edits',
      'Basic AI website edits with fair-use limits',
      'SEO basics and search indexing setup',
      '5 social posts per month',
      '2 blog posts per month',
      'Version history and growth metrics',
    ],
  },
  {
    name: 'Growth',
    label: 'For active growth',
    price: '$249',
    originalPrice: '$498',
    period: '/month',
    copy: 'Turn your website into a visibility and content engine across search and AI discovery.',
    cta: 'Choose Growth',
    ctaRoute: '/login',
    featured: true,
    features: [
      '1 published website',
      'Lifetime free hosting',
      'Unlimited manual edits',
      'Higher AI website edit limits',
      'SEO, AEO, and GEO optimization',
      'Search indexing workflows',
      '10 social posts per month',
      '10 blog posts per month',
      'Growth recommendations and priority processing',
    ],
  },
  {
    name: 'Enterprise',
    label: 'For teams and scale',
    price: 'Contact us',
    period: '',
    copy: 'Custom setup for multi-site teams, high-volume content, governance, and dedicated support.',
    cta: 'Talk to sales',
    features: [
      'Multiple websites',
      'Lifetime free hosting options',
      'Higher AI and content limits',
      'Advanced SEO/AEO/GEO workflows',
      'Custom reporting and team roles',
      'SSO/SAML and audit requirements',
      'SLA and priority support',
      'Dedicated onboarding',
      'Custom deployment/runtime setup',
    ],
  },
]
</script>

<style scoped>
.pricing-root {
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  color: var(--text-primary);
}
.pricing-orb {
  position: fixed;
  pointer-events: none;
  border-radius: 999px;
  filter: blur(70px);
  z-index: 0;
}
.pricing-orb-a {
  width: 720px;
  height: 720px;
  top: -240px;
  left: -220px;
  background: radial-gradient(circle, rgba(124,109,250,.42), rgba(124,109,250,.08) 55%, transparent 72%);
}
.pricing-orb-b {
  width: 680px;
  height: 680px;
  right: -230px;
  bottom: -260px;
  background: radial-gradient(circle, rgba(52,211,153,.28), rgba(56,189,248,.08) 48%, transparent 72%);
}
.pricing-nav {
  position: sticky;
  top: 0;
  z-index: 10;
  background: rgba(15,15,24,.72);
  border-bottom: 1px solid rgba(255,255,255,.07);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}
.pricing-nav-inner {
  max-width: 1200px;
  height: 64px;
  margin: 0 auto;
  padding: 0 clamp(1rem, 4vw, 3rem);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  position: relative;
}
.pricing-nav-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 2px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 999px;
  padding: 4px;
}
.pricing-nav-pill {
  padding: .4rem 1.1rem;
  border-radius: 999px;
  font-size: .875rem;
  font-weight: 500;
  color: rgba(255,255,255,0.65);
  text-decoration: none;
  transition: background .15s, color .15s;
}
.pricing-nav-pill:hover,
.pricing-nav-pill.router-link-active {
  background: rgba(255,255,255,0.1);
  color: #fff;
}
.pricing-logo {
  display: inline-flex;
  align-items: center;
  gap: .6rem;
  color: var(--text-primary);
  font-weight: 800;
  text-decoration: none;
  letter-spacing: -.02em;
}
.pricing-logo img {
  width: 190px;
  height: auto;
  display: block;
}
.pricing-nav-actions {
  display: flex;
  align-items: center;
  gap: .75rem;
}
.pricing-nav-link,
.pricing-signin {
  color: var(--text-secondary);
  font-size: .88rem;
  font-weight: 700;
  text-decoration: none;
  transition: all .2s;
}
.pricing-nav-link:hover {
  color: var(--text-primary);
}
.pricing-signin {
  padding: .55rem 1.25rem;
  border-radius: 999px;
  background: var(--glass-bg);
  border: var(--glass-border);
  backdrop-filter: blur(12px);
}
.pricing-signin:hover {
  color: var(--text-primary);
  background: var(--glass-bg-hover);
  border: var(--glass-border-hi);
}
.pricing-hero,
.pricing-section {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding-left: clamp(1rem, 4vw, 3rem);
  padding-right: clamp(1rem, 4vw, 3rem);
}
.pricing-hero {
  padding-top: clamp(5rem, 10vw, 8rem);
  padding-bottom: clamp(3rem, 7vw, 5rem);
  text-align: center;
}
.pricing-eyebrow {
  margin: 0 0 .75rem;
  color: var(--accent);
  font-size: .72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .18em;
}
.pricing-hero h1 {
  max-width: 920px;
  margin: 0 auto;
  color: #fff;
  font-size: clamp(2.6rem, 6.5vw, 5.5rem);
  font-weight: 870;
  line-height: .95;
  letter-spacing: -.06em;
}
.pricing-hero h1 span {
  display: block;
  background: linear-gradient(135deg, var(--accent) 0%, #38bdf8 48%, #34d399 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.pricing-sub {
  max-width: 720px;
  margin: 1.5rem auto 0;
  color: var(--text-secondary);
  font-size: clamp(1rem, 1.5vw, 1.18rem);
  line-height: 1.75;
}
.pricing-section {
  padding-top: clamp(2rem, 5vw, 4rem);
  padding-bottom: clamp(2rem, 5vw, 4rem);
}
.pricing-section-head {
  max-width: 640px;
  margin-bottom: 2rem;
}
.pricing-section-head-wide {
  max-width: 760px;
}
.pricing-section-head h2,
.pricing-value-card h2 {
  margin: 0;
  color: var(--text-primary);
  font-size: clamp(1.8rem, 3.5vw, 2.8rem);
  font-weight: 820;
  line-height: 1.1;
  letter-spacing: -.04em;
}
.pricing-section-head p:not(.pricing-eyebrow) {
  margin: .85rem 0 0;
  color: var(--text-secondary);
  line-height: 1.7;
}
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
  align-items: stretch;
}
.pricing-card {
  position: relative;
  border-radius: 24px;
  padding: 1.5rem;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.pricing-card-featured {
  background:
    linear-gradient(180deg, rgba(124,109,250,.22), rgba(52,211,153,.08)),
    var(--glass-bg);
  border: 1px solid rgba(124,109,250,.45);
  box-shadow: 0 24px 70px rgba(124,109,250,.22);
}
.pricing-badge {
  position: absolute;
  top: 1.1rem;
  right: 1.1rem;
  padding: .35rem .65rem;
  border-radius: 999px;
  background: rgba(52,211,153,.14);
  color: #7cf1bc;
  border: 1px solid rgba(52,211,153,.25);
  font-size: .68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .08em;
}
.pricing-plan-label {
  margin: 0 0 .7rem;
  color: var(--text-muted);
  font-size: .72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .14em;
}
.pricing-card h3,
.pricing-addon-card h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.45rem;
  font-weight: 820;
  letter-spacing: -.035em;
}
.pricing-price {
  margin: 1.3rem 0 .75rem;
  display: flex;
  align-items: flex-end;
  gap: .35rem;
  flex-wrap: wrap;
}
.pricing-price-original {
  color: var(--text-muted);
  font-size: clamp(1rem, 2vw, 1.4rem);
  font-weight: 600;
  text-decoration: line-through;
  opacity: .6;
  align-self: center;
  margin-bottom: .1rem;
}
.pricing-price span {
  color: #fff;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 900;
  letter-spacing: -.06em;
  line-height: .9;
}
.pricing-price small {
  color: var(--text-muted);
  font-size: .9rem;
  margin-bottom: .15rem;
}
.pricing-plan-copy {
  margin: 0 0 1.35rem;
  color: var(--text-secondary);
  font-size: .9rem;
  line-height: 1.65;
}
.pricing-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: .85rem 1rem;
  border-radius: 999px;
  background: var(--glass-bg-hover);
  border: var(--glass-border);
  color: var(--text-primary);
  font-weight: 800;
  text-decoration: none;
  transition: all .2s;
}
.pricing-cta:hover {
  transform: translateY(-1px);
  border: var(--glass-border-hi);
}
.pricing-cta-primary {
  background: linear-gradient(135deg, var(--accent), #34d399);
  border: none;
  box-shadow: 0 16px 40px rgba(124,109,250,.28);
}
.pricing-features {
  list-style: none;
  margin: 1.35rem 0 0;
  padding: 1.25rem 0 0;
  border-top: var(--glass-border);
  display: flex;
  flex-direction: column;
  gap: .72rem;
}
.pricing-features li {
  display: flex;
  align-items: flex-start;
  gap: .55rem;
  color: var(--text-secondary);
  font-size: .86rem;
  line-height: 1.45;
}
.pricing-features span {
  color: var(--success);
  font-weight: 900;
}
.pricing-addon-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
}
.pricing-addon-card {
  border-radius: 22px;
  padding: 1.4rem;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1rem;
}
.pricing-addon-icon {
  width: 48px;
  height: 48px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 1.4rem;
  font-weight: 900;
}
.pricing-addon-card p:not(.pricing-plan-label) {
  margin: .65rem 0 0;
  color: var(--text-secondary);
  font-size: .9rem;
  line-height: 1.65;
}
.pricing-inline-link {
  display: inline-flex;
  margin-top: 1rem;
  color: var(--accent);
  font-size: .9rem;
  font-weight: 800;
  text-decoration: none;
}
.pricing-inline-link:hover {
  opacity: .8;
}
.pricing-value {
  padding-bottom: clamp(4rem, 8vw, 7rem);
}
.pricing-value-card {
  border-radius: 28px;
  padding: clamp(2rem, 4vw, 3rem);
  background: linear-gradient(135deg, rgba(124,109,250,.14), rgba(52,211,153,.07));
}
.pricing-value-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 2rem;
}
.pricing-value-grid div {
  padding: 1rem;
  border-radius: 16px;
  background: var(--glass-bg);
  border: var(--glass-border);
}
.pricing-value-grid strong,
.pricing-value-grid span {
  display: block;
}
.pricing-value-grid strong {
  color: var(--text-primary);
  margin-bottom: .35rem;
}
.pricing-value-grid span {
  color: var(--text-secondary);
  font-size: .88rem;
  line-height: 1.6;
}
.glass {
  background: var(--glass-bg);
  border: var(--glass-border);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
}

@media (max-width: 980px) {
  .pricing-grid {
    grid-template-columns: 1fr;
  }
  .pricing-addon-grid,
  .pricing-value-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .pricing-nav-inner {
    height: auto;
    min-height: 64px;
    align-items: flex-start;
    padding-top: .85rem;
    padding-bottom: .85rem;
  }
  .pricing-nav-actions {
    gap: .6rem;
  }
  .pricing-logo img {
    width: 150px;
  }
  .pricing-hero {
    text-align: left;
  }
  .pricing-addon-card {
    grid-template-columns: 1fr;
  }
}
</style>
